import '~/stylesheets/react-components/completed-projects-map.scss';
import Header from '~/javascripts/components/CompletedProjectsMap/Header';
import LoadingGif from '~/images/icons/loading.gif';
import MapViewButtons from '~/javascripts/components/CompletedProjectsMap/MapViewButtons';
import NoImpactCta from '~/javascripts/components/CompletedProjectsMap/NoImpactCta';
import SearchAutocomplete from '~/javascripts/components/CompletedProjectsMap/SearchAutocomplete';
import UserHeader from '~/javascripts/components/CompletedProjectsMap/UserHeader';
import cx from 'classnames';
import mapboxgl from 'mapbox-gl';
import { observer, useLocalObservable } from 'mobx-react';
import { pauseForSpecs } from '~/javascripts/utils/testing';
import { pulsingDot } from '~/javascripts/utils/mapbox';
import { render } from 'react-dom';
import { useLayoutEffect, useRef } from 'react';

let tooltipPopup, map;

const Map = observer(
  ({
    accessToken,
    currentUser,
    currentUserName,
    disableAutoSelect,
    disableSearch,
    displayMode,
    inProgressDonationCount,
    initialSearch,
    showAllCwImpact,
    showUserImpact,
    siteConstantsFromContentful,
    hideHeader,
  }) => {
    const mapEl = useRef(null);
    const terrainView = 'clwjnp1k903oh01qg58zv5rxr';
    const satelliteView = 'ckcmah0ll1g611hpjkm391b6k';
    const mapDefaultZoomLevel = 13;
    const savedSearchHash = $(location).attr('hash').substring(1);
    const searchTermFromParams = new URLSearchParams(
      window.location.search
    ).get('search');

    const localStore = useLocalObservable(() => ({
      loading: true,
      noResults: false,
      searchTerm: initialSearch || searchTermFromParams || '',
      lastUpdatedDate: null,
      mapStyle: terrainView,
      projects: null,
      projectCountryCount: null,
      projectsToDisplay: function () {
        if (currentUser && this.showUserImpact) {
          return this.userProjects;
        } else {
          return this.projects;
        }
      },
      activeMapView: 'terrain',
      setActiveMapView: function (viewId) {
        this.activeMapView = viewId;
      },
      showUserImpact: showUserImpact || false,
      userProjects: null,
    }));

    useLayoutEffect(() => {
      if (window.disableMap) {
        return;
      }
      createAndConfigureMap();
      getLastUpdatedDate();

      if (currentUser) {
        getUserProjectCountryCount();
      }

      document.addEventListener('ModalOpened', () => {
        setTimeout(() => {
          if (map) {
            map.resize();
          }
        }, 100);
      });
    }, []);

    const createAndConfigureMap = () => {
      mapboxgl.accessToken = accessToken;
      map = new mapboxgl.Map({
        container: mapEl.current,
        style: `mapbox://styles/charitywater/${terrainView}`,
        center: [0, 0],
        zoom: 2,
        maxZoom: mapDefaultZoomLevel,
      });

      window.map = map;
      window.mapboxgl = mapboxgl;

      map.scrollZoom.disable();
      map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

      map.on('style.load', () => {
        if (!map.hasImage('pulsing-dot')) {
          map.addImage('pulsing-dot', pulsingDot(80), {
            pixelRatio: 2,
          });
        }

        addProjects();
      });

      pauseForSpecs(() => {
        map.on('idle', () => {
          localStore.loading = false;
        });
      });
    };

    const getUserProjectCountryCount = () => {
      $.get(
        `/api/v1/projects/country_count?user_id=${currentUser.id}`,
        (response) => {
          localStore.projectCountryCount = response.body;
        }
      );
    };

    const addProjects = () => {
      if (currentUser) {
        getUserProjects();
      }
      if (showAllCwImpact) {
        getProjects();
      }
    };

    const search = (value) => {
      if (disableSearch) return null;

      localStore.loading = true;
      localStore.noResults = false;
      localStore.searchTerm = value;

      updateUrl();
      searchProjects();
    };

    const updateUrl = () => {
      const url = new URL(window.location);
      if (localStore.searchTerm && localStore.searchTerm != '') {
        url.searchParams.set('search', localStore.searchTerm);
      } else {
        url.searchParams.delete('search');
      }
      window.history.pushState('', '', url);
    };

    const sourceUrlFor = (path) =>
      `/api/v1/projects${path}?search=${localStore.searchTerm}`;

    const sourceUrlForUser = (path) =>
      `/api/v1/projects${path}?search=${localStore.searchTerm}&user_id=${currentUser.id}`;

    const searchProjects = () => {
      let sourceUrl;

      if (currentUser && localStore.showUserImpact) {
        sourceUrl = sourceUrlForUser('/');
        $.getJSON(sourceUrl, (data) => {
          localStore.userProjects = data;
          map.getSource('projects').setData(localStore.userProjects);
          fitMapAndFocusProject();
        });
      } else {
        sourceUrl = sourceUrlFor('/');
        $.getJSON(sourceUrl, (data) => {
          localStore.projects = data;
          map.getSource('projects').setData(localStore.projects);
          fitMapAndFocusProject();
        });
      }
    };

    const getUserProjects = () => {
      const sourceUrl = sourceUrlForUser('/');
      $.getJSON(sourceUrl, (data) => {
        localStore.userProjects = data;

        if (localStore.showUserImpact) {
          displayProjects();
        }
      });
    };

    const getProjects = () => {
      const sourceUrl = sourceUrlFor('/');
      $.getJSON(sourceUrl, (data) => {
        localStore.projects = data;

        if (!localStore.showUserImpact) {
          displayProjects();
        }
      });
    };

    const displayProjects = () => {
      addOrSetMapSource();
      maybeAddMapLayers();
      handleSavedSearch();
      fitMapAndFocusProject();
    };

    const addOrSetMapSource = () => {
      if (map.getSource('projects')) {
        map.getSource('projects').setData(localStore.projectsToDisplay());
      } else {
        map.addSource('projects', {
          type: 'geojson',
          data: localStore.projectsToDisplay(),
          cluster: true,
          clusterMaxZoom: 10,
          clusterRadius: 50,
          clusterMinPoints: 100,
        });
      }
    };

    const maybeAddMapLayers = () => {
      if (map.getLayer('projects')) {
        return;
      }
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'projects',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#FFC42E',
            100,
            '#FFC42E',
            750,
            '#FFC42E',
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40,
          ],
        },
      });

      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'projects',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      });

      map.addLayer({
        id: 'projects',
        type: 'circle',
        source: 'projects',
        filter: ['!has', 'point_count'],
        paint: {
          'circle-color': '#FFC42E',
          'circle-radius': {
            stops: [
              [2, 3],
              [8, 5],
            ],
          },
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1,
        },
      });

      inspectClustersOnClick();
      bindPointsMouseEventsFor('projects');
      bindPointsMouseEventsFor('clusters');
    };

    const inspectClustersOnClick = () => {
      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        });
        const clusterId = features[0].properties.cluster_id;
        map
          .getSource('projects')
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom + 1,
            });
          });
      });
    };

    const bindPointsMouseEventsFor = (id) => {
      map.on('click', id, (e) => {
        showProject(e.features[0].properties.id, e);
      });

      map.on('mouseenter', id, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', id, () => {
        map.getCanvas().style.cursor = '';
      });
    };

    const handleSavedSearch = () => {
      if (
        savedSearchHash !== '' &&
        !savedSearchHash.startsWith('closed-campaign-')
      ) {
        $.getJSON(
          `/api/v1/saved_searches?saved_search_hash=${savedSearchHash}`,
          (data) => {
            search(data['search_term']);
          }
        );
      }
    };

    const showImpactToggle = currentUser && showAllCwImpact;

    const fitMapAndFocusProject = () => {
      if (tooltipPopup) tooltipPopup.remove();
      removeAllPopups();

      const sourceUrl = localStore.showUserImpact
        ? sourceUrlForUser('/metadata')
        : sourceUrlFor('/metadata');

      $.getJSON(sourceUrl, (data) => {
        if (data) {
          map.fitBounds(data.bounds, {
            padding: {
              bottom: 120,
              left: 120,
              right: 120,
              top: 120,
            },
            linear: false,
            maxZoom: 11,
            speed: 0.4,
            duration: 1800,
          });
          if (!disableAutoSelect || localStore.searchTerm != '') {
            showProject(data.focus);
          }
        } else {
          if (localStore.searchTerm != '') {
            localStore.noResults = true;
          }
          map.easeTo({
            zoom: 2,
            speed: 0.4,
            curve: 1,
            duration: 1000,
          });
        }
      });
    };

    const getLastUpdatedDate = () => {
      $.get('/api/v1/projects/last_updated', (response) => {
        localStore.lastUpdatedDate = response.body;
      });
    };

    const tooltip = (response) => (
      <>
        <a
          className={cx(
            'flex flex-row md-flex-col no-underline text-left map-project-content relative',
            { 'pointer-events-none': displayMode === 'lab-history-table' }
          )}
          href={response.link}
        >
          <img
            className='object-cover md-w-auto rounded'
            src={response.photo || ''}
          />
          <div className='flex flex-col justify-center md-justify-start py-2 mx-2 md-ml-0'>
            <h2 className='body-m mb-nudge text-cw-black'>{response.name}</h2>
            <div className='flex body-2xs items-center font-thin text-grey-60 info'>
              <span className='tick-circle-icon' />
              {response.formatted_publish_date}
              &nbsp;
              <>
                <span className='people-served-icon' />
                {response.people_served}
              </>
            </div>
          </div>
          {displayMode !== 'lab-history-table' && (
            <div className='details-link flex flex-row justify-between items-center md-border md-border-b-0 md-border-l-0 md-border-r-0 border-cw-black absolute md-relative'>
              <p className='label-2xs pt-1 show-for-medium-up pt-1'>
                View Details
              </p>
              <icon className='cw-icon-angle-right pt-0 md-pt-1' />
            </div>
          )}
        </a>
      </>
    );

    const showProject = (id, event) => {
      $.get(`/api/v1/projects/${id}`, (response) => {
        const coordinates = [response.longitude, response.latitude];

        if (event) {
          while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
          }
        }

        map.flyTo({
          center: coordinates,
          speed: 0.4,
        });

        removeAllPopups();

        tooltipPopup = new mapboxgl.Popup({
          className: 'project-tooltip',
          closeButton: false,
        })
          .setLngLat(coordinates)
          .setHTML('')
          .addTo(map);

        const tooltipContent = document.querySelector(
          '.project-tooltip .mapboxgl-popup-content'
        );
        if (tooltipContent) {
          render(tooltip(response), tooltipContent);
        }
      });
    };

    const removeAllPopups = () => {
      const popups = document.getElementsByClassName('mapboxgl-popup');
      if (popups.length) {
        Array.from(popups).map((popup) => popup.remove());
      }
    };

    const toggleProjects = ({ showUserImpact }, e) => {
      localStore.showUserImpact = showUserImpact;
      if (e?.target) {
        localStore.searchTerm = '';
        removeAllPopups();
        updateUrl();
        searchProjects();
      } else {
        search(localStore.searchTerm);
      }

      const projectsToDisplay = localStore.showUserImpact
        ? localStore.userProjects
        : localStore.projects;
      map.getSource('projects').setData(projectsToDisplay);
      fitMapAndFocusProject();
    };

    return (
      <div className='theme-maji'>
        {!hideHeader && (
          <>
            {localStore.showUserImpact ? (
              <UserHeader
                currentUser={currentUser}
                currentUserName={currentUserName}
                projectCountryCount={localStore.projectCountryCount}
                userProjects={localStore.userProjects}
              />
            ) : (
              <Header
                lastUpdatedDate={localStore.lastUpdatedDate}
                siteConstantsFromContentful={siteConstantsFromContentful}
              />
            )}
          </>
        )}
        <div className='map-container relative'>
          {localStore.loading && (
            <div className='map-loading'>
              <img className='m-auto' src={LoadingGif} />
            </div>
          )}
          {localStore.noResults && (
            <div className='no-project-results absolute z-50 bg-cw-white text-center p-1 rounded text-small'>
              We&apos;re sorry, we couldn&apos;t find any projects that matched
              your search. Please try again.
            </div>
          )}
          {showImpactToggle && (
            <div className='impact-toggle toggle-buttons absolute'>
              <a
                className={cx('text-cw-black capitalize body-s font-normal', {
                  active: !localStore.showUserImpact,
                })}
                onClick={(e) => toggleProjects({ showUserImpact: false }, e)}
              >
                All Impact
              </a>
              <a
                className={cx('text-cw-black capitalize body-s font-normal', {
                  active: localStore.showUserImpact,
                })}
                onClick={(e) => toggleProjects({ showUserImpact: true }, e)}
              >
                My Impact
              </a>
            </div>
          )}
          {!disableSearch && (
            <SearchAutocomplete
              removeAllPopups={removeAllPopups}
              search={search}
              searchTerm={localStore.searchTerm}
              showUserImpact={localStore.showUserImpact}
              userId={currentUser?.id}
            />
          )}

          {localStore.showUserImpact &&
            localStore.userProjects?.features?.length == 0 && (
              <NoImpactCta
                currentUser={currentUser}
                inProgressDonationCount={inProgressDonationCount}
              />
            )}
          <div ref={mapEl} />
          <MapViewButtons
            activeMapView={localStore.activeMapView}
            map={map}
            mapDefaultZoomLevel={mapDefaultZoomLevel}
            satelliteView={satelliteView}
            setActiveMapView={localStore.setActiveMapView}
            terrainView={terrainView}
          />
        </div>
      </div>
    );
  }
);

export default Map;
